/**
 * The card fee, computed the same way the server computes it.
 *
 * ── Why this arithmetic exists twice ────────────────────────────────────────
 *
 * The server is the authority — it is what writes the number onto the invoice.
 * But a figure that only appears after you press Draft is a figure nobody can
 * decide about, and the decision is exactly what the checkbox is for. So the
 * screen works it out too, and the two have to agree to the cent.
 *
 * They agree because this is the same formula, not a simplification of it. The
 * temptation is `base × rate`, and it is wrong: the processor takes its
 * percentage of the amount it collects, which includes the fee. Solving
 * `(base + s) × pct + fixed = s` gives the amount that nets exactly `base`:
 *
 *     s = (base × pct + fixed) ÷ (1 − pct)
 *
 * On a $100 invoice at 2.9% + 30c the naive version is 9 cents short — small,
 * always in the same direction, and therefore the kind that survives for years.
 */
export function surchargeCents(baseCents, { surchargeBps = 0, surchargeFixedCents = 0 } = {}) {
  const pct = Number(surchargeBps || 0) / 10000
  const fixed = Number(surchargeFixedCents || 0)
  if (baseCents <= 0 || (pct <= 0 && fixed <= 0)) return 0
  // No solution at or above 100%: the fee would have to be infinite to cover
  // itself. Zero is a wrong answer; NaN on an invoice is a worse one.
  if (pct >= 1) return 0
  return Math.round((baseCents * pct + fixed) / (1 - pct))
}

/** "2.9% + $0.30", for a label somebody can check against Stripe's pricing. */
export function describeRate({ surchargeBps = 0, surchargeFixedCents = 0 } = {}, currency = "USD") {
  const parts = []
  if (surchargeBps > 0) parts.push(`${(surchargeBps / 100).toFixed(2).replace(/\.?0+$/, "")}%`)
  if (surchargeFixedCents > 0) {
    parts.push(new Intl.NumberFormat(undefined, { style: "currency", currency }).format(surchargeFixedCents / 100))
  }
  return parts.join(" + ")
}
